/**
 * Виды замечаний «требует внимания»: русская подпись и «кому и что делать».
 *
 * Ключи ставит бэк (`Attention.kind`, `cashflow/sources/*`). Незнакомый вид не
 * прячем и не переименовываем: показываем сам ключ в секции действий — молча
 * потерянное замечание хуже некрасивого.
 */
export interface AttentionKindInfo {
  title: string;
  hint: string;
  /** `action` — надо что-то сделать в 1С/настройках, `info` — справка. */
  section: "action" | "info";
}

export const ATTENTION_KINDS: Record<string, AttentionKindInfo> = {
  order_no_payment_facts: {
    title: "Заказы без факта оплаты в регистре",
    hint: "Закупки/бухгалтерия: проверить, оплачен ли заказ; в календаре стоит вся сумма документа",
    section: "action",
  },
  orders_without_terms: {
    title: "Договоры без условий оплаты",
    hint: "Закупки: заполнить доп. реквизиты договора (предоплата / при отгрузке / отсрочка), тогда суммы разложатся по траншам",
    section: "action",
  },
  orders_beyond_window: {
    title: "Старые открытые заказы",
    hint: "Бухгалтерия: закрыть или отменить в 1С; на календарь не влияют",
    section: "action",
  },
  order_no_receipt_date: {
    title: "Заказы без даты поступления",
    hint: "Закупки: заполнить дату поступления в заказе",
    section: "action",
  },
  order_to_pay_exceeds_total: {
    title: "Регистр оплат расходится с заказом",
    hint: "Бухгалтерия: проверить заказ и оплаты в 1С",
    section: "action",
  },
  order_fact_below_total: {
    title: "Сумма регистра меньше суммы заказа",
    hint: "Бухгалтерия: проверить заказ в 1С",
    section: "action",
  },
  contract_terms_invalid: {
    title: "Некорректные условия оплаты в договорах",
    hint: "Закупки: исправить доп. реквизиты договора",
    section: "action",
  },
  kontragent_not_found: {
    title: "Контрагент не найден в справочнике",
    hint: "Бухгалтерия: контрагент удалён или помечен на удаление",
    section: "action",
  },
  request_unapproved_past: {
    title: "Просроченные неутверждённые заявки",
    hint: "Финдиректор: утвердить (появится расход) или отменить в 1С",
    section: "action",
  },
  request_no_register_rows: {
    title: "Заявки без строки в платёжном календаре 1С",
    hint: "Бухгалтерия: перепровести заявку",
    section: "action",
  },
  inflow_plan_overdue: {
    title: "Просроченные планы поступлений",
    hint: "Продажи: клиент задерживает оплату — уточнить срок и передвинуть план в 1С",
    section: "action",
  },
  inflow_plan_past: {
    title: "Планы поступлений без основания с прошедшей датой",
    hint: "Продажи/бухгалтерия: проверить, поступили ли деньги; 1С такие планы не закрывает",
    section: "action",
  },
  inflow_plan_other_article: {
    title: "Планы поступлений с нестандартной статьёй ДДС",
    hint: "Бухгалтерия: проверить статью",
    section: "action",
  },
  inflow_plan_basis_untracked: {
    title: "Планы поступлений с основанием, которого нет в регистре расчётов",
    hint: "Справочно: проверить оплату нечем — учтены как планы без основания",
    section: "info",
  },
  payment_order_stale: {
    title: "Старые неоплаченные платёжки",
    hint: "Бухгалтерия: скорее всего оплачена, сопоставить с выпиской",
    section: "action",
  },
  loan_no_schedule: {
    title: "Кредит без графика платежей",
    hint: "Бухгалтерия: заполнить график в договоре кредита",
    section: "action",
  },
  no_settlement_accounts: {
    title: "Организация без расчётных счетов",
    hint: "Настройки: проверить ИНН и счета в УНФ",
    section: "action",
  },
  org_not_in_plan: {
    title: "Запись по организации вне плана",
    hint: "Настройки финплана",
    section: "action",
  },
  payment_order_transfer: {
    title: "Переводы между своими счетами",
    hint: "Справочно: не расход компании",
    section: "info",
  },
  request_covered: {
    title: "Заявки, уже учтённые графиком кредита или зарплатой",
    hint: "Справочно: в календарь не добавлены, чтобы не удвоить",
    section: "info",
  },
  payment_order_covered: {
    title: "Платёжки, уже учтённые графиком кредита или зарплатой",
    hint: "Справочно",
    section: "info",
  },
  account_skipped: {
    title: "Счета, не входящие в остаток",
    hint: "Справочно: депозитные и недействительные",
    section: "info",
  },

  // Источник «Поставщики: план закупок» (закупки сырья под заказы на производство).
  production_horizon: {
    title: "План производства: горизонт",
    hint: "До какой недели заведены заказы на производство в 1С; дальше закупки не считаются",
    section: "info",
  },
  production_divisions: {
    title: "Незакрытое производство по подразделениям",
    hint: "Справочно: сколько заказов на производство не закрыто, в разбивке по подразделениям",
    section: "info",
  },
  production_not_today: {
    title: "План закупок считается только на сегодня",
    hint: "Остатки и заказы в 1С есть только на текущий момент — выберите сегодняшнюю дату",
    section: "info",
  },
  prod_orders_backlog: {
    title: "Заказы на производство из прошлых недель",
    hint: "Производство: закрыть выпущенное в 1С; спрос по ним поставлен в текущую неделю",
    section: "info",
  },
  prod_orders_beyond_window: {
    title: "Старые заказы на производство с остатком",
    hint: "Производство: закрыть или отменить в 1С; в расчёт не идут",
    section: "action",
  },
  prod_orders_cancelled: {
    title: "Отменённые заказы на производство с остатком",
    hint: "Производство: проверить в 1С",
    section: "action",
  },
  prod_orders_no_week: {
    title: "Заказы на производство без номера недели",
    hint: "Производство: заполнить реквизит «Номер недели» в заказе",
    section: "action",
  },
  prod_line_spec_conflict: {
    title: "У продукции заказа две строки с разными спецификациями",
    hint: "Производство: оставить одну спецификацию в заказе; строки не посчитаны",
    section: "action",
  },
  prod_line_no_spec: {
    title: "Строки заказов без спецификации",
    hint: "Производство: указать спецификацию в строке заказа; материалы под них не посчитаны",
    section: "action",
  },
  spec_ambiguous: {
    title: "Несколько активных спецификаций",
    hint: "Технолог: оставить одну активную спецификацию без характеристики",
    section: "action",
  },
  spec_missing_forced: {
    title: "Нет спецификации у выдуваемого флакона",
    hint: "Технолог: завести активную спецификацию (преформа + подложка)",
    section: "action",
  },
  spec_missing: {
    title: "Спецификация не найдена",
    hint: "Технолог: проверить ссылку на спецификацию в 1С",
    section: "action",
  },
  stock_negative: {
    title: "Отрицательные остатки на складах",
    hint: "Склад: разобраться в 1С; в расчёт не берутся",
    section: "action",
  },
  orders_dead: {
    title: "Мёртвые заказы поставщикам",
    hint: "Закупки: закрыть или передвинуть дату поступления в 1С; приход по ним не ждём",
    section: "action",
  },
  incoming_no_arrival: {
    title: "Заказы поставщикам без даты поступления",
    hint: "Закупки: заполнить дату поступления; приход по ним не учтён",
    section: "action",
  },
  incoming_beyond_window: {
    title: "Старые открытые заказы поставщикам",
    hint: "Закупки: закрыть в 1С; приход по ним не учтён",
    section: "action",
  },
  material_no_price: {
    title: "Материалы без цены",
    hint: "Закупки: нет приходных за окно и нет открытого заказа — закупка в деньгах не посчитана",
    section: "action",
  },
  material_no_supplier: {
    title: "Материалы без поставщика",
    hint: "Закупки: поставщик не определился ни по приходным, ни по заказам",
    section: "action",
  },
  material_zero_cost: {
    title: "Закупки с нулевой суммой",
    hint: "Проверить цену и количество в 1С",
    section: "action",
  },
  supplier_terms_conflict: {
    title: "Разные условия оплаты в договорах поставщика",
    hint: "Закупки: привести условия в договорах к одним; платёж поставлен на дату прихода",
    section: "action",
  },
  supplier_lead_conflict: {
    title: "Разный срок поставки в договорах поставщика",
    hint: "Закупки: привести срок поставки в договорах к одному; взят наибольший",
    section: "action",
  },
  prod_orders_far_future: {
    title: "Заказы на производство с неделей далеко за горизонтом",
    hint: "Производство: проверить номер недели в заказе; в расчёт не идут",
    section: "action",
  },
  incoming_far_future: {
    title: "Заказы поставщикам с датой поступления далеко за горизонтом",
    hint: "Закупки: проверить дату поступления в заказе; приход по ним не учтён",
    section: "action",
  },
  bom_leaf_of_produced: {
    title: "Полуфабрикат в спецификации без подспецификации",
    hint: "Технолог: указать подспецификацию в строке; компонент учтён как производимый",
    section: "info",
  },
  suppliers_no_terms_planned: {
    title: "Поставщики без условий оплаты (план закупок)",
    hint: "Закупки: заполнить условия в договоре; платёж поставлен на дату прихода",
    section: "action",
  },
  bom_line_skipped: {
    title: "Строки спецификаций не типа «Материал»",
    hint: "Технолог: строка спецификации не типа «Материал» — в расчёт закупок не включена",
    section: "action",
  },
  bom_nom_unknown: {
    title: "Спецификации ссылаются на неизвестную номенклатуру",
    hint: "Технолог: строка спецификации ссылается на номенклатуру, которой нет в справочнике 1С",
    section: "action",
  },
  bom_unit: {
    title: "Единицы в спецификациях не пересчитываются",
    hint: "Технолог: исправить единицу строки или завести коэффициент у материала; строка не закуплена",
    section: "action",
  },
  bom_unit_warning: {
    title: "Единицы в спецификациях: предупреждения",
    hint: "Технолог: проверить единицы; строка посчитана с допущением",
    section: "action",
  },
  bom_sub_spec_conflict: {
    title: "Одна номенклатура с разными подспецификациями в спецификации",
    hint: "Технолог: в спецификации одна номенклатура встречается с разными подспецификациями — уточнить состав",
    section: "action",
  },
  bom_spec_empty: {
    title: "Пустые спецификации",
    hint: "Технолог: в спецификации нет строк — заполнить состав",
    section: "action",
  },
  order_bom_unusable: {
    title: "Состав заказа без спецификации не разобран",
    hint: "Производство: указать спецификацию или выпуск в заказе",
    section: "action",
  },
  bom_cycle: {
    title: "Цикл в спецификациях",
    hint: "Технолог: разорвать цикл; ветка не посчитана",
    section: "action",
  },
  bom_depth: {
    title: "Слишком глубокое дерево спецификаций",
    hint: "Технолог: упростить дерево спецификации; глубже определённого уровня разбор остановлен",
    section: "action",
  },
};

/** Описание вида; незнакомый ключ → подпись = сам ключ, секция действий. */
export function attentionKindInfo(kind: string): AttentionKindInfo {
  return ATTENTION_KINDS[kind] ?? { title: kind, hint: "", section: "action" };
}
